// RIBBON MOUNT (R1 Row 2, 2026-07-20; R2 Row 1 + scope chip same day) — the
// LIVE host for the whole two-row ribbon.
//
// This is the seam between the pure ribbon skeleton (R0: rowsets as data,
// handler KEYS, mirror KEYS) and the real editor. It owns three things and
// nothing else:
//   1. the HANDLER RESOLUTION TABLE — every 'namespace.verb' key any rowset
//      names, resolved to the house editor verbs through the ONE commit
//      grammar (useSelectionProps.apply: now/soon/defer + commitNow);
//   2. the MIRROR BAG — useSelectionProps vocabulary passed through as-is,
//      plus the ribbon's derived additions. Row 1 reads EFFECTIVE values:
//      per-char override first (selectionStyle — the run under the cursor),
//      object prop as the fallback — the selection-aware read rule the scope
//      chip makes visible;
//   3. the DISABLED-STATE POLICY (host policy, executed by the ribbon):
//      · no selection      → Row 2 UNMOUNTS entirely (R2.5 — rows=[1]); Row 1
//                            shows the page tools, all live (poster light
//                            included — it lives in the page morph now);
//      · locked selection  → Row-2 styling + arrange + position disabled
//                            (the 'object' group stays live: Lock must be
//                            reachable to unlock) AND the whole Row-1 morph
//                            disabled — locked means no styling, no photo
//                            surgery, no flips;
//      · multi-selection   → 'depth' disabled (patchTextFx would write
//                            data.fx onto the throwaway ActiveSelection);
//      · image / group     → 'depth' disabled (R1 conservatism, unchanged);
//      · 'painter' is ALWAYS disabled — object.formatPainter is a SHELL
//        this phase (R4 implements); painterActive mirrors false.
//
// R2 flipped rows to [1, 2]: Row 1 morphs by selection kind through the
// rowset registry (text / shape / image / icon / none) and the OLD TOP
// TOOLBAR IS RETIRED — its text branch became Row 1's text set, its photo
// machinery rides usePhotoActions, its Background strip became pageRow1,
// its icon tint became iconRow1, Ungroup and the smart-object Edit button
// became showWhen groups on shapeRow1.
//
// R2.5 (flyout redesign, owner review):
//   · Row 2 renders ONLY while a selection exists (rows=[1] at kind 'none' —
//     the row UNMOUNTS, no reserved space; the canvas ResizeObserver
//     reflows). Poster light moved to pageRow1 so the empty state keeps its
//     one page-scoped control; Row 2 lost the group entirely (no twins).
//   · Row 1 text controls are flyout buttons now — new keys:
//     text.setFontSizeLive (size slider 'defer' ticks), text.clearCharStyles,
//     ui.openTextureExplorer (renders the house TextureExplorerModal here).
//     Spacing sliders went 'soon'→'defer' (flyout sliders commit ONE entry
//     on release via history.commitNow — the house drag law).
//
// R3 (Row-2 section rework, owner's seven directives, 2026-07-20):
//   · Row 2 is FIVE labeled sections now — STYLE | DEPTH | ARRANGE | OBJECT
//     | POSITION (rowset dividers render the visible | rules).
//   · COLOR moved from Row 1 to Style: ONE button for every object.
//     object.pickColor routes BY KIND — text keeps the per-char law verbatim
//     (forSelectionOrObject → per-char fill while a range is live, else the
//     whole-object setTextFill recipe), shape → changeFillColor, icon →
//     changeIconColor (the tint verb — the only color verb that DOES
//     anything on an icon); single photos are honestly disabled. Recipes
//     (object.setFillRecipe) stay text-only, and the panel is Color-pane-
//     only for non-text (solidOnlyWhen) so no tab can lie.
//   · OUTLINE is one flyout (swatches + width slider + none) on the same
//     setBorder rule; width slides 'defer' + ONE commit on release.
//   · OPACITY folded into a button + slider flyout (canonical home intact).
//   · EFFECTS button REMOVED (owner §3). PANEL PINNED MODEL (owner
//     2026-07-23): the drawer NEVER auto-opens — the Row-2 More-options
//     toggle (panelOpen/onTogglePanel props) + flyout footers + Position
//     open it and PIN it; ✕ unpins.
//   · BEVEL/3D are split buttons: face = the old checkbox verbs (defaults
//     50/55, ONE entry), chip = tuning flyout on the drawer's exact patch
//     grammar (depth.setBevel* / depth.set3D* / depth.setPlate*).
//   · ARRANGE is one flyout menu (layer moves / align to page / group +
//     ungroup via canGroup/canUngroup mirrors).
//   · STYLES★ moved to Style — text opens the explorer; shapes/icons open the
//     kind-aware Styles tab (U2); photos disabled with an honest tooltip.
//
// UNIVERSAL EFFECTS (U2, owner 2026-07-21 "apply all the same effects to shapes
// and icons"): Row 2's EFFECTS faces + the Bevel depth face now work for
// shapes/icons, gated PER KIND against lib/fx/capabilityMatrix (READ ONLY —
// fxOff_<id>/fxOffTitle_<id> mirrors; the Outline face hides for shapes; the 3D
// face stays matrix-gated until U3 via a DYNAMIC read). fx.toggleEffect /
// fx.applyParams route by kind: text keeps applyTextEffect/removeTextEffect
// verbatim, shape/icon run the object-generic applyEffect/removeEffect. Depth
// is enabled for icons now (bevel is an fx step on any alpha); photos/groups/
// multi stay conservative.
//   · Row 1 grew the superscript/subscript flyout (text.setScript →
//     engine setTextScript; 3D-gated via is3dActive like the drawer).
//   · Format painter parks INSIDE Object, dimmed per-control
//     (painterDisabled mirror) — no more always-disabled group.
//
// GLOBAL UNDO (owner, 2026-07-21: "we always need an undo redo button
// available"; app-bar lane B0 same day: the pair moved from the ribbon's
// left rail into BAR 0 — AppBar.jsx, rendered here above the rows) —
// state.undoAvailable/redoAvailable mirror editor.canUndo()/canRedo()
// (reactive: every commit bumps historyIndex → facade identity → re-render,
// selection kind irrelevant), 'history.undo'/'history.redo' exit any live
// typing session first (fabric commits the session entry on exitEditing)
// then drive onUndo/onRedo. The bottom-bar Undo/Redo pair is RETIRED — the
// rail is the one always-visible home. Icon tint is DEBOUNCED here
// (applyIconTint): the engine verb self-commits per call, so drag ticks
// must coalesce before the verb, not at the commit.
//
// THE SCOPE CHIP (concept §2.3) is live here: scope is DERIVED at render
// time from the active object's editing selection (grapheme space —
// selectionStart/End), exactly the range routeToLiveSelection styles. The
// mirror refreshes on text:selection:changed / editing entered + exited
// (useSelectionProps listeners), so the chip tracks the highlight live.
// Widening drives fabric's own selection APIs (searchWordBoundary /
// collapse) and keeps the hidden textarea in sync — the textbox never
// leaves editing.
import React, { useEffect, useRef, useState } from 'react';
import Ribbon from './Ribbon';
import {
  registerBuiltinRowsets, textRow1, shapeRow1, imageRow1, iconRow1, pageRow1,
} from './rowsets/index.mjs';
import { useSelectionProps, isTextType } from '../hooks/useSelectionProps';
import { getFontWeights } from '../fonts/fontCatalog';
import { BG_PRESETS } from '../primitives/toolbarPrimitives';
import { FX_BEVEL_DEFAULTS } from '../lib/fx/fxBevel.js';
import { FX_EXTRUDE_DEFAULTS, FX_EXTRUDE_PLATE_DEFAULTS } from '../lib/fx/fxExtrude.js';
// Universal effects (U2): the ONE gate table (kind × effect → mechanism|reason).
// READ ONLY — the engine owns writes; the ribbon consults it to enable/disable
// tiles and pick plain-words reasons. Pure data/functions (no fabric).
import { effectCapability, SLOT_EFFECT_IDS } from '../lib/fx/capabilityMatrix.js';
// The text-effect defs — the ONE source for the Row-2 EFFECTS section's toggle
// defaults (owner, 2026-07-20). applyTextEffect(id, effectDefaults(def)) is the
// exact tile-tap the drawer's EffectTiles does; the faces read tilePreview()
// from the same defs (EffectTileFace) so ribbon + drawer never drift.
import { EFFECT_DEFS, effectDefaults } from '../inspector/text/effectDefs';
import TextureExplorerModal from '../inspector/text/TextureExplorerModal';
import ObjectSwapModal from '../ObjectSwapModal';
import { PaintPickerModalHost } from '../PaintPickerModal';
import { SmartToolsBrowserHost } from '../SmartToolsBrowser';
import { registerPaletteCanvasGetter } from '../paintModalController';
import usePhotoActions from './usePhotoActions';
// BAR 0 (app-bar lane B0, 2026-07-21): the always-visible strip ABOVE the
// two rows — Save/Undo/Redo/Print/Poster + the account cluster. Rendered
// here so it shares the mount's mirror bag and handler table (the poster
// light + background chip kept their exact pageRow1 wiring).
import AppBar from './AppBar';
import './ribbon.css';

// The host registers the rowsets exactly once (module scope — same contract
// the lab uses; registration is idempotent by design).
registerBuiltinRowsets();

/** True when any object on the canvas (recursively) opts into the shared
 *  poster light (an fx step with lightMode 'poster') — the 'light' group's
 *  ACTIVE dot. Render-time derivation: every commit re-renders this tree
 *  (historyIndex bump), so the walk is fresh exactly when it must be. */
function derivePosterLightOn(editor) {
  const objs = editor?.canvas?.getObjects?.();
  if (!objs) return false;
  let found = false;
  const walk = (list) => {
    for (const o of list) {
      if (found) return;
      const fx = Array.isArray(o.data?.fx) ? o.data.fx : null;
      if (fx && fx.some((s) => s?.params?.lightMode === 'poster')) {
        found = true;
        return;
      }
      if (typeof o.getObjects === 'function') walk(o.getObjects());
    }
  };
  walk(objs);
  return found;
}

// fabric's own word alphabet (IText reNonWord) — the chip's snap test and
// the widen verb MUST agree with the engine's dblclick word selection, or
// "Word" would mean two different things on the same canvas.
const NON_WORD_RE = /[ \n.,;!?\-]/;
const isWordCh = (ch) => ch != null && !NON_WORD_RE.test(ch);

/** The scope the standard controls are ABOUT to style (concept §2.3):
 *  'all'  — no live range → whole-object writes;
 *  'word' — the range is exactly one word (fabric boundaries);
 *  'chars'— any other live range (count in graphemes — _text space, the
 *  same space setSelectionStyles consumes). */
function deriveScope(editor) {
  const obj = editor?.canvas?.getActiveObject?.();
  if (!obj || !isTextType(obj.type) || !obj.isEditing) return { kind: 'all' };
  const a = obj.selectionStart ?? 0;
  const b = obj.selectionEnd ?? 0;
  const start = Math.min(a, b);
  const end = Math.max(a, b);
  if (end <= start) return { kind: 'all' };
  const chars = obj._text?.length ? obj._text : Array.from(obj.text || '');
  const inner = chars.slice(start, end);
  const snapped = inner.length > 0
    && inner.every(isWordCh)
    && !isWordCh(chars[start - 1])
    && !isWordCh(chars[end]);
  if (snapped) return { kind: 'word', word: inner.join('') };
  return { kind: 'chars', count: end - start };
}

const WEIGHT_NAMES = {
  100: 'Thin', 300: 'Light', 400: 'Regular', 500: 'Medium',
  600: 'SemiBold', 700: 'Bold', 800: 'ExtraBold', 900: 'Black',
};

// Curve preset chips → changeTextCurve(amount, shape). Amounts are the
// HOUSE presets — the drawer's Shape tiles use the same starting bends
// (CURVE_TILES: arc 50, circle 100, wave 50), so chip and tile land on the
// same look; the drawer slider fine-tunes from there.
const CURVE_PRESETS = {
  straight: [0, undefined],
  arc: [50, 'arc'],
  circle: [100, 'circle'],
  wave: [50, 'wave'],
};

const SHAPE_TYPE_LABELS = {
  rect: 'Rectangle', circle: 'Circle', triangle: 'Triangle', ellipse: 'Ellipse',
  polygon: 'Polygon', line: 'Line', path: 'Shape', group: 'Group',
};

// R3 section-groups (Row 2): the 'object' section stays live while locked —
// Lock must be reachable to unlock (painter inside it dims per-control).
// 'effects' is a styling section (text effects) — folds into both policy sets.
const OBJECT_GROUPS = ['style', 'effects', 'depth', 'arrange', 'object', 'position'];
const STYLING_GROUPS = ['style', 'effects', 'depth', 'arrange', 'position'];
// Row-1 group ids per morph — the locked policy disables the whole morph
// (derived from the rowsets so a group rename can't silently skip the law).
const ROW1_GROUP_IDS = {
  text: textRow1.groups.map((g) => g.id),
  shape: shapeRow1.groups.map((g) => g.id),
  image: imageRow1.groups.map((g) => g.id),
  icon: iconRow1.groups.map((g) => g.id),
  none: pageRow1.groups.map((g) => g.id),
};

export default function RibbonMount({
  editor, selectedObjects = [], onOpenInspector, onOpenSmartPanel, onOpenPageSettings,
  // Bar 0's host bag (StudioCoreEditor): { isDirty, saveBusy, printBusy,
  // onSave, onPrint, onOpenPrintJobs } — save/print stay editor-page
  // machinery; the bar only fires them.
  appBar,
  // Panel pinned-model (owner 2026-07-23): the drawer never auto-opens;
  // this pair drives the Row-2 toggle button + its lit state.
  panelOpen = false,
  onTogglePanel,
}) {
  const { objProps, apply, commitNow, setBorder } = useSelectionProps(editor, selectedObjects);
  const photo = usePhotoActions(editor);
  // Non-Rect corner bake (lane N2): the slider has NO live tick (each would
  // re-run the WASM offset + replace the object), so shape.bakeCorners only
  // STASHES the dragged radius here; the bake fires ONCE on release, flushed
  // from the wrapped history.commitNow (the slider's pointer-up key) — ONE
  // undo entry, the honest 'rounds when you let go' contract.
  const pendingRoundRef = useRef(null);
  // Texture Explorer (color flyout's "Browse all textures…") — the house
  // modal rendered HERE (reuse, never clone); pure UI state.
  const [textureExplorerOpen, setTextureExplorerOpen] = useState(false);
  // The SAME modal in pickMode for an effect's 'fill' param (the sticker
  // border's 'Texture…' row — sticker-shapes lane): {effectId, paramKey} |
  // null. The pick writes {type:'texture', id, scale} through the effect's
  // own applyTextEffect commit path — the modal is an input method here,
  // never a second writer.
  const [fxTexturePick, setFxTexturePick] = useState(null);

  // Object-swap modal (owner directive §3): { mode: 'shape' | 'icon' } | null.
  // Row 1's "Change shape/icon" buttons open it; the pick fires the kind's swap
  // verb (below) and closes. Mounts in this file's own render (self-contained —
  // no StudioCoreEditor collision), the TextureExplorerModal pattern.
  const [swapModal, setSwapModal] = useState(null);

  // Icon tint coalescer (global-undo audit, 2026-07-21): changeIconColor
  // SELF-COMMITS (its remove+add batch is one history entry per call) and
  // re-fetches the icon from the network per call — so PaintPicker drag
  // ticks routed straight through it landed one entry (and one fetch) PER
  // TICK. Trailing debounce: a drag stroke settles into ONE recolor → ONE
  // entry; discrete picks pay a 350 ms beat the fetch dwarfs anyway. Unmount
  // flushes the pending tint so a pick never silently vanishes.
  const iconTintTimerRef = useRef(null);
  const iconTintPendingRef = useRef(null);
  const iconTintCtxRef = useRef({ editor: null, apply: null });
  iconTintCtxRef.current = { editor, apply };
  const applyIconTint = (hex, mode) => {
    if (typeof hex !== 'string') return;
    // Leading-edge fast path (undo-lane debt fix): a DISCRETE pick (swatch /
    // hex / eyedropper / recent chip) arrives 'now' — apply IMMEDIATELY, no
    // 350ms wait. Cancel any pending trailing tick first so a drag's last
    // 'soon' tick and this discrete pick can never double-fire the recolor.
    // Live drag ticks arrive 'soon'/'defer' and take the trailing branch below,
    // so the one-entry-per-drag law (and its single iconify fetch) is untouched.
    if (mode === 'now') {
      if (iconTintTimerRef.current) { clearTimeout(iconTintTimerRef.current); iconTintTimerRef.current = null; }
      iconTintPendingRef.current = null;
      const ctx = iconTintCtxRef.current;
      // 'defer' — the engine verb commits its own single entry; apply() here
      // only re-mirrors after it lands.
      if (ctx.apply) ctx.apply(() => ctx.editor?.changeIconColor(hex), 'defer');
      return;
    }
    iconTintPendingRef.current = hex;
    if (iconTintTimerRef.current) clearTimeout(iconTintTimerRef.current);
    iconTintTimerRef.current = setTimeout(() => {
      iconTintTimerRef.current = null;
      const v = iconTintPendingRef.current;
      iconTintPendingRef.current = null;
      const ctx = iconTintCtxRef.current;
      // 'defer' — the engine verb commits its own single entry; apply() here
      // only re-mirrors after it lands.
      if (v != null) ctx.apply?.(() => ctx.editor?.changeIconColor(v), 'defer');
    }, 350);
  };
  useEffect(() => () => {
    // Unmount flush: run the pending recolor directly (it self-commits) —
    // mirrors are gone with the component, so apply() is pointless here.
    if (iconTintTimerRef.current) {
      clearTimeout(iconTintTimerRef.current);
      iconTintTimerRef.current = null;
      const v = iconTintPendingRef.current;
      iconTintPendingRef.current = null;
      if (v != null) iconTintCtxRef.current.editor?.changeIconColor?.(v);
    }
  }, []);
  // Palette source for the color library's "From this poster" row — the
  // workspace object carries the live fabric canvas; re-register per editor
  // identity (facade rebuilds on every history commit).
  useEffect(
    () => registerPaletteCanvasGetter(() => editor?.getWorkspace?.()?.canvas ?? null),
    [editor],
  );

  const first = selectedObjects[0];
  const isMulti = selectedObjects.length > 1;
  // Row 1 morphs on this (registry lookup); Row 2 never morphs — for it the
  // kind only feeds data-selection-kind + the disabled policy.
  //   multi  → 'text' when EVERY member is text (the engine text verbs hit
  //            them all — a real classroom move), else 'shape' ("things on
  //            the page": count readout + flips);
  //   icon   → its own morph (tint) — photos and icons share a fabric type
  //            but not a toolset.
  const selectionKind = !first
    ? 'none'
    : isMulti
      ? (selectedObjects.every((o) => isTextType(o.type)) ? 'text' : 'shape')
      : isTextType(first.type)
        ? 'text'
        : first.type === 'image'
          ? (first.isIcon === true ? 'icon' : 'image')
          : 'shape';
  const locked = selectedObjects.length > 0 && selectedObjects.every((o) => o.locked === true);

  // ---- effective text reads (the selection-aware read rule) ----
  // Per-char override first (selectionStyle mirrors the highlighted run,
  // sparse — absent key = none or mixed), object prop as fallback. When a
  // range is LIVE these drive Row 1, and the scope chip says so.
  const selStyle = objProps.selectionStyle || {};
  const effFontFamily = selStyle.fontFamily ?? objProps.fontFamily ?? 'Arial';
  const effFontSize = selStyle.fontSize ?? objProps.fontSize ?? 32;
  const effFontWeight = selStyle.fontWeight ?? objProps.fontWeight ?? 400;
  const effFontStyle = selStyle.fontStyle ?? objProps.fontStyle ?? 'normal';
  const effUnderline = selStyle.underline ?? objProps.underline ?? false;
  const effLinethrough = selStyle.linethrough ?? objProps.linethrough ?? false;
  // string-guarded: a gradient/texture fill is an OBJECT on the fabric side —
  // the swatch face then renders from textFill, not this hex.
  const effTextColor = typeof selStyle.fill === 'string' && selStyle.fill.startsWith('#')
    ? selStyle.fill
    : (typeof objProps.fill === 'string' ? objProps.fill : '#000000');

  // ---- mirror bag: useSelectionProps vocabulary + ribbon additions ----
  const fx = objProps.textEffects || {};
  // The DUAL slot bag (U1: data.effects on shapes/icons, data.textEffects on
  // text — one identity per kind). Drives the EFFECTS section dot + the
  // non-text toggle's active read; for text it IS textEffects, so nothing
  // shifts there.
  const fxBag = objProps.effects || {};
  const bevelOn = (objProps.fxBevelDepth ?? 0) > 0;
  const threeDOn = (objProps.fxExtrudeDepth ?? 0) > 0;

  // ---- universal-effects gating (U2): per-effect, per-kind, from the matrix ----
  // The effect-capable kind. TEXT (single or multi) keeps its EXACT prior
  // behavior: the whole text half delegates to the untouched text engine, so a
  // multi-text ActiveSelection (whose objectKind is null) must NOT gate it —
  // pin it to 'text'. Everything else reads the live object's kind: a single
  // shape/icon → the matrix; a group / photo / mixed-or-same multi (objectKind
  // null/photo) → the matrix's honest "single object" / "photo" reason
  // (conservative this lane — the surface disables, the engine can do more).
  const fxKind = selectionKind === 'text' ? 'text' : (objProps.objectKind ?? null);
  const THREE_D_FIGHTERS = new Set(['hollow', 'trace', 'sticker']);
  const fxGates = {};
  for (const id of SLOT_EFFECT_IDS) {
    const cap = effectCapability(fxKind, id);
    const capOff = cap.mode === 'unavailable';
    // Hollow/Trace/Sticker also fold under an active 3D bake (the die-cut /
    // see-through look the extrude replaces). For shapes/icons 3D can't be on
    // (matrix-denied), so this only ever bites text — but it's kind-agnostic
    // and honest either way.
    const threeGate = THREE_D_FIGHTERS.has(id) && objProps.is3dActive === true;
    fxGates[`fxOff_${id}`] = capOff || threeGate;
    fxGates[`fxOffTitle_${id}`] = capOff
      ? cap.reason
      : (threeGate ? 'Turn off 3D to use this effect' : '');
  }
  // Outline face HIDDEN for shapes (the Border tool owns shape edges — no
  // twins), present + fx-gated for icons/text.
  const fxOutlineHidden = fxKind === 'shape';
  // 3D depth face — matrix-gated for non-text until U3 lands the silhouette
  // source. DYNAMIC read: when U3 flips extrude to a real mechanism the face
  // enables itself with no edit here.
  const extrudeCap = effectCapability(fxKind, 'extrude');
  const threeDDisabled = extrudeCap.mode === 'unavailable';
  // Sticker-plate patches send the WHOLE plate object (patchTextFx merges
  // shallow — the drawer's patchPlate rule, reused for the 3D flyout).
  const patchPlate = (changes, mode = 'defer') => {
    const plate = objProps.fxExtrudePlate || { ...FX_EXTRUDE_PLATE_DEFAULTS };
    apply(() => editor?.patchTextFx('extrude', { plate: { ...plate, ...changes } }), mode);
  };
  const workspace = editor?.getWorkspace?.();
  const wsW = workspace?.width || 0;
  const wsH = workspace?.height || 0;
  const inch = (u) => {
    const v = u / 72; // 72 canvas units = 1 in (print300 contract)
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
  };
  const dpi = objProps.effectiveDPI;
  const smartRecipe = onOpenSmartPanel ? (editor?.getSelectedSmartObject?.()?.recipe || null) : null;
  // Script state (Row 1 x² flyout) — the drawer's exact read: the
  // highlighted run's shared deltaY (sparse selectionStyle), else normal.
  const effDeltaY = selStyle.deltaY ?? 0;
  const state = {
    ...objProps,
    // ---- Row 1 (text) — effective values override the raw mirrors ----
    scope: deriveScope(editor),
    fontFamily: effFontFamily,
    fontSize: Math.round(effFontSize),
    fontWeight: effFontWeight,
    fontStyle: effFontStyle,
    underline: effUnderline,
    linethrough: effLinethrough,
    textColor: effTextColor,
    textScript: effDeltaY < 0 ? 'super' : effDeltaY > 0 ? 'sub' : 'normal',
    fontWeightOptions: getFontWeights(effFontFamily)
      .map((w) => ({ value: String(w), label: WEIGHT_NAMES[w] || String(w) })),
    sizeUnit: 'pt', // fixed this phase — R4 brings in/cm + "Readable from…"
    curvePreset: (objProps.curveAmount || 0) === 0 ? 'straight' : (objProps.curveShape || 'arc'),
    subjectActive: (objProps.phonicsMode ?? 'none') !== 'none',
    // Style-section dot: a live style recipe (slot effect or rich fill) —
    // solid recolors don't count (every text has SOME color; noise).
    hasTextStyle: !!(fx.shadow?.id || fx.stroke?.id)
      || ['linear-gradient', 'radial-gradient', 'texture'].includes(objProps.textFill?.type),
    // ---- Row 1 morphs (shape / image / icon / page) ----
    shapeTypeLabel: !first ? null
      : isMulti && selectionKind === 'shape' ? `${selectedObjects.length} selected`
        : SHAPE_TYPE_LABELS[first.type] || (first.type ? first.type[0].toUpperCase() + first.type.slice(1) : 'Shape'),
    shapeIsRect: !isMulti && first?.type === 'rect',
    // Corners-bake gate (lane N2): a SINGLE cornered fill shape — triangle /
    // polygon / path (incl. baked Paths). Rect keeps its live rx/ry slider;
    // circle/ellipse have no corners (no slider — a control that can't act is a
    // lie); line has no fill. The slider it gates bakes on release.
    shapeCanBakeCorners: !isMulti
      && ['triangle', 'polygon', 'polyline', 'path'].includes(String(first?.type || '').toLowerCase()),
    // The bake is destructive (no persistent rx to read back) — the slider is
    // an 'apply rounding' action that resets to 0 after each commit.
    cornerBakeRadius: 0,
    // Change-shape gate (owner directive §3): a SINGLE swappable primitive only
    // — a group / smart object (a Group) / multi-selection has no one silhouette
    // to swap, so the button must not appear there (a control that sometimes
    // cannot act is a lie). Icons/photos are 'image' → excluded by the allowlist.
    canSwapShape: !isMulti && !!first
      && ['rect', 'circle', 'ellipse', 'triangle', 'polygon', 'path', 'line']
        .includes(String(first.type || '').toLowerCase()),
    canUngroup: !isMulti && first?.type === 'group',
    smartRecipeName: smartRecipe?.name || null,
    smartEditLabel: smartRecipe ? `Edit ${smartRecipe.name}` : null,
    frameShape: objProps.frameShape || 'none', // mirrored (stable-bag rule)
    removeBgLabel: photo.removeBgBusy ? 'Removing…' : 'Remove BG',
    dpiBadge: selectionKind === 'image' && dpi != null
      ? {
          text: dpi >= 150 ? 'Print-ready' : dpi >= 100 ? 'May look soft' : 'Will print blurry',
          tone: dpi >= 150 ? 'good' : dpi >= 100 ? 'warn' : 'bad',
          title: `~${dpi} DPI at this print size — 150+ recommended`,
        }
      : null,
    posterSizeLabel: wsW && wsH ? `${inch(wsW)} × ${inch(wsH)} in` : null,
    // Bar 0 Poster flyout: raw inch numbers for the editable W×H fields
    // (72 units = 1 in — the same print300 contract as the label above) and
    // the design's export-DPI target (workspace data bag, default 300).
    posterWidthIn: wsW ? wsW / 72 : null,
    posterHeightIn: wsH ? wsH / 72 : null,
    exportDPI: editor?.getExportDPI?.() ?? 300,
    backgroundPresets: BG_PRESETS,
    // ---- Row 2 (R3 sections) ----
    // THE color button's face + panel gates, routed by kind (see header):
    // text = the effective (per-char-aware) color + its FillValue recipe;
    // icons show the tint; shapes the flat fill; photos disable honestly.
    objColor: selectionKind === 'text'
      ? effTextColor
      : selectionKind === 'icon'
        ? (typeof objProps.iconColor === 'string' ? objProps.iconColor : '#000000')
        : (typeof objProps.fill === 'string' ? objProps.fill : '#000000'),
    objFillRecipe: selectionKind === 'text' ? (objProps.textFill ?? null) : null,
    colorSolidOnly: selectionKind !== 'text',
    colorUnavailable: selectionKind === 'image',
    // Educator-first title for the house paint modal (owner 2026-07-21): the
    // Row-2 Color chip opens the ONE modal now, so it carries a kind-true label.
    colorTitle: selectionKind === 'text' ? 'Text color'
      : selectionKind === 'icon' ? 'Icon color' : 'Fill',
    // Styles★ (moved from Row 1): text opens the full explorer; shapes/icons
    // open the kind-aware Styles tab (curated recipes via editor.applyStyle).
    // Photos keep their own look (filter tools) — honestly disabled.
    stylesUnavailable: selectionKind === 'image',
    // outline = the flat face of the border mirror (setBorder rule)
    outlineColor: objProps.borderColor ?? '#000000',
    outlineWidth: objProps.borderWidth ?? 0,
    // opacity flyout face lights when the object is anything but solid
    opacityBelowFull: (objProps.opacity ?? 1) < 1,
    // dots
    bevelOn,
    threeDOn,
    hasDepth: bevelOn || threeDOn,
    // 3D face gate (matrix `extrude` cell, dynamic — U3 flips it for shapes/
    // icons). Bevel needs no per-control gate: it's an fx step on any alpha
    // (proven), enabled wherever the Depth GROUP is (text/shape/icon).
    threeDDisabled,
    threeDDisabledTitle: extrudeCap.reason || 'Turn off 3D to use this effect',
    // EFFECTS section (owner 2026-07-20; universal U2 2026-07-21). hasFxSlot
    // lights the section dot when any slot effect is live — reads the DUAL bag
    // so it lights for every kind. The eight per-effect gates (fxOff_<id> +
    // fxOffTitle_<id>) come straight from the capability matrix per kind, and
    // the Outline face hides for shapes.
    hasFxSlot: !!(fxBag.shadow?.id || fxBag.stroke?.id),
    ...fxGates,
    fxOutlineHidden,
    panelOpen,
    // the effect-capable kind itself — lets controls speak the right noun
    // ("letters" for text, "object" for shapes/icons) without re-deriving
    fxKind,
    // arrange menu conditionals: Group needs a multi-selection, Ungroup a
    // single fabric Group (the same mirror the shape morph shows).
    canGroup: isMulti,
    // Combine gate (lane N2): 2+ selected, EVERY member a fill shape (incl.
    // baked Paths; line excluded — no area to boolean). A mixed selection with
    // an image/text/group can't combine, so the items stay hidden (the house
    // 'a control that can't act is a lie' law — same showWhen posture as Group).
    canCombineShapes: selectedObjects.length >= 2
      && selectedObjects.every((o) => ['rect', 'circle', 'ellipse', 'triangle', 'polygon', 'polyline', 'path']
        .includes(String(o?.type || '').toLowerCase())),
    // page-scoped poster light: read the ENGINE, not the selection mirror —
    // the dial must work (and read true) with nothing selected.
    posterLightAngle: editor?.getPosterLight?.()?.angle ?? 135,
    posterLightOn: derivePosterLightOn(editor),
    // format painter is a SHELL until R4 — parked in Object, dimmed
    painterActive: false,
    painterDisabled: true,
    locked,
    // History cluster mirrors (global-undo lane): reactive by construction —
    // every commit bumps historyIndex, which rebuilds the editor facade,
    // which re-renders this mount at EVERY selection kind (none included).
    undoAvailable: !!editor?.canUndo?.(),
    redoAvailable: !!editor?.canRedo?.(),
    workspaceFill: typeof workspace?.fill === 'string' ? workspace.fill : '#ffffff',
    // The page paint as a FillValue for the PaintPicker face/tabs: a string
    // fill passes through; a fabric Gradient (changeBackgroundGradient's
    // output) reads back as a linear-gradient recipe so the chip face and
    // the Gradient pane tell the truth. Angle from the coords' quadrant —
    // the engine knows three directions (vertical/horizontal/diagonal).
    workspacePaint: (() => {
      const f = workspace?.fill;
      if (typeof f === 'string') return f;
      if (f?.colorStops?.length) {
        const c = f.coords || {};
        const dx = (c.x2 ?? 0) - (c.x1 ?? 0);
        const dy = (c.y2 ?? 0) - (c.y1 ?? 0);
        const angle = dx && dy ? 45 : dx ? 0 : 90;
        return {
          type: 'linear-gradient',
          angle,
          stops: f.colorStops.map((s) => ({ offset: s.offset, color: s.color })),
        };
      }
      return '#ffffff';
    })(),
  };

  // ---- disabled policy (see header; painter dims per-control now) ----
  const disabled = new Set();
  if (!first) {
    OBJECT_GROUPS.forEach((id) => disabled.add(id));
  } else if (locked) {
    STYLING_GROUPS.forEach((id) => disabled.add(id));
    (ROW1_GROUP_IDS[selectionKind] || []).forEach((id) => disabled.add(id));
  }
  // Depth (bevel/3D) writes onto the active object's data.fx via patchTextFx —
  // a multi-select ActiveSelection or a Group would take the write on the
  // throwaway wrapper, so both stay disabled. PHOTOS are matrix-denied
  // (bevel-on-photo is future work). ICONS and shapes KEEP Depth now (U2):
  // bevel is an fx step on any alpha (proven); the 3D face inside stays
  // matrix-gated per-control until U3.
  if (isMulti || first?.type === 'group' || (first?.type === 'image' && first?.isIcon !== true)) {
    disabled.add('depth');
  }

  // ---- the handler resolution table ----
  const alignVerb = { left: 'alignLeft', centerH: 'alignCenterH', right: 'alignRight' };
  // History cluster: exit a live typing session FIRST — fabric commits the
  // session's one entry on exitEditing (object:modified fires iff the text
  // changed), so Undo-while-typing means "close the session, then undo it",
  // never "restore a half-typed ghost". Mirrors the keyboard rule (the
  // window keydown handler no-ops while isEditing; the button is the mouse
  // path to the same law).
  const exitTypingThen = (fn) => () => {
    const obj = editor?.canvas?.getActiveObject?.();
    if (obj?.isEditing) obj.exitEditing();
    fn();
  };
  const handlers = {
    // ======== the persistent history cluster (left rail) ========
    'history.undo': exitTypingThen(() => editor?.onUndo()),
    'history.redo': exitTypingThen(() => editor?.onRedo()),
    // ======== Row 1 — scope chip (concept §2.3) ========
    // Widening drives fabric's own selection machinery so the chip, the
    // dblclick gesture and routeToLiveSelection can never disagree; the
    // hidden textarea is kept in sync and editing state is never dropped.
    'scope.set': (target) => {
      const obj = editor?.canvas?.getActiveObject?.();
      if (!obj || !isTextType(obj.type) || !obj.isEditing) return;
      if (target === 'word') {
        const start = Math.min(obj.selectionStart ?? 0, obj.selectionEnd ?? 0);
        const end = Math.max(obj.selectionStart ?? 0, obj.selectionEnd ?? 0);
        const s = obj.searchWordBoundary(start, -1);
        const e = Math.max(s, obj.searchWordBoundary(Math.max(start, end - 1), 1));
        obj.selectionStart = s;
        obj.selectionEnd = e;
      } else {
        // 'all': collapse the live range — no range means the standard
        // controls fall through to their whole-object bodies (the
        // routeToLiveSelection false path IS "All text").
        obj.selectionStart = obj.selectionEnd = obj.selectionEnd ?? 0;
      }
      obj._fireSelectionChanged(); // canvas 'text:selection:changed' → mirrors refresh
      obj._updateTextarea();
      obj.renderCursorOrSelection?.();
      editor?.canvas?.requestRenderAll?.();
    },

    // ======== Row 1 — text verbs (all selection-aware via the engine's
    // routeToLiveSelection: a live range gets per-char styles, else the
    // whole object — the chip announces which) ========
    // Font family/weight load the face first and commit their own entry
    // ('defer' — the immediate commit would be a dedupe no-op).
    'text.setFontFamily': (family) => apply(() => editor?.changeFontFamily(family), 'defer'),
    // Typed size ('soon' — bursts coalesce; engine bounds 4–2000 so existing
    // outsized designs stay reachable by keyboard).
    'text.setFontSize': (v) => apply(() => editor?.changeFontSize(Math.max(4, Math.min(2000, v))), 'soon'),
    // The size flyout's slider: 'defer' ticks; RibbonFlyout's slider fires
    // history.commitNow on release → ONE entry per drag.
    'text.setFontSizeLive': (v) => apply(() => editor?.changeFontSize(Math.max(4, Math.min(2000, v))), 'defer'),
    'text.setFontWeight': (v) => apply(() => editor?.changeFontWeight(parseInt(v, 10)), 'defer'),
    'text.toggleBold': () => apply(() => editor?.changeFontWeight(effFontWeight > 500 ? 400 : 700), 'defer'),
    'text.toggleItalic': () => apply(() => editor?.changeFontStyle(effFontStyle === 'italic' ? 'normal' : 'italic')),
    'text.toggleUnderline': () => apply(() => editor?.changeFontUnderline(!effUnderline)),
    'text.toggleStrikethrough': () => apply(() => editor?.changeFontLinethrough(!effLinethrough)),
    'text.setLetterCase': (mode) => apply(() => editor?.setLetterCase(mode)),
    // Superscript/subscript (owner §6): the engine's native verbs — the
    // highlighted run while editing, the whole string otherwise. 3D-gated
    // in the rowset (is3dActive), so this never fires against a bake.
    'text.setScript': (kind) => apply(() => editor?.setTextScript(kind)),
    // The way OUT of per-char styling (FillSection's quiet link, same verb).
    'text.clearCharStyles': () => apply(() => editor?.clearSelectionTextStyle()),
    'ui.openTextureExplorer': () => setTextureExplorerOpen(true),
    'ui.togglePanel': () => onTogglePanel?.(),
    // An effect 'fill' param's texture picker (sticker border). Fired by the
    // flyout's 'Texture…' row; the flyout closes itself, the modal opens here.
    'fx.pickFillTexture': (effectId, paramKey) => setFxTexturePick({ effectId, paramKey }),
    // Picking the live list mode again toggles the list off (same rule the
    // lab mocked); the flyout also offers an explicit 'none' row.
    'text.setList': (mode) => apply(() => editor?.setTextList(state.listMode === mode ? 'none' : mode)),
    'text.setAlign': (v) => apply(() => editor?.changeTextAlign(v)),
    // Spacing flyout sliders: 'defer' ticks + commitNow on release (was
    // 'soon' as R2 number fields — the flyout made them true sliders).
    'text.setLetterSpacing': (v) => apply(() => editor?.changeCharSpacing(v), 'defer'),
    'text.setLineHeight': (v) => apply(() => editor?.changeLineHeight(v), 'defer'),
    // Presets are one-click looks; multi-line text is refused by the engine
    // (isCurveEligible) exactly as the drawer tiles are — the drawer's hint
    // explains the fix, the chip simply no-ops.
    'text.setCurvePreset': (p) => {
      const preset = CURVE_PRESETS[p];
      if (preset) apply(() => editor?.changeTextCurve(preset[0], preset[1]));
    },
    'text.fitToBox': () => apply(() => editor?.autoFitText('grow')),
    // Subject group (R2 placeholder): straight into the ELA tools accordion
    // (internal id 'phonics'); R3's manifest registry replaces this.
    'subject.openTools': () => onOpenInspector?.('effects', 'phonics'),
    // Styles★ (universal U2): text opens the full explorer inside its Style
    // tab; shapes/icons open the kind-aware Styles tab (StylesTab → curated
    // shape/icon recipes via editor.applyStyle — text-only styles never appear
    // there because the preset lists are per-kind). Photos are gated upstream.
    'ui.openStylesExplorer': () => (selectionKind === 'text'
      ? onOpenInspector?.('effects', 'styles-explorer')
      : onOpenInspector?.('styles')),

    // ======== Row 1 — image / icon morphs (usePhotoActions machinery) ========
    'image.crop': photo.openCropModal,
    'image.replace': photo.openReplacePicker,
    'image.removeBackground': photo.removeBg,
    'ui.openFiltersPanel': () => onOpenInspector?.('effects'),
    'image.setFrameShape': (v) => apply(() => editor?.applyFrameShape(v === 'none' ? null : v), 'defer'),
    // Debounced (see applyIconTint): the engine verb self-commits per call,
    // so raw drag ticks would each cost a history entry + an iconify fetch.
    // The mode ('now' discrete / 'soon' drag tick) rides through so a plain
    // pick applies on the leading edge instead of waiting the full 350ms.
    'image.setIconTint': (hex, mode) => applyIconTint(hex, mode),
    // Change icon (owner directive §3): open the swap picker (icon browser).
    // The pick fires editor.swapIcon (new silhouette at the CURRENT tint, all
    // style + effects carried — the changeIconColor engine posture).
    'icon.openSwap': () => setSwapModal({ mode: 'icon' }),

    // ======== Row 1 — shape morph ========
    // Corner radius is a SLIDER now (owner directive §2): defer ticks during
    // drag, ONE commit on release (history.commitNow from the slider) — the
    // house slider grammar, replacing the old 'soon' number box.
    'shape.setCornerRadius': (v) => apply(() => editor?.changeCornerRadius(v), 'defer'),
    // Non-Rect corners (lane N2): DESTRUCTIVE bake, so no live tick — stash the
    // dragged radius and let the release (history.commitNow) flush the bake.
    'shape.bakeCorners': (v) => { pendingRoundRef.current = Number(v) || 0; },
    // Change shape (owner directive §3): open the swap picker (shape grid). The
    // pick fires editor.swapShapeType (new outline fitted to the current box,
    // all paint + effects carried, ONE undo entry).
    'shape.openSwap': () => setSwapModal({ mode: 'shape' }),
    'arrange.ungroup': () => apply(() => editor?.ungroup()),
    'ui.openSmartPanel': () => onOpenSmartPanel?.(),

    // ======== page verbs (B0: fired from Bar 0's Poster flyout now — the
    // empty-state morph keeps only its hint; keys and grammars unchanged) ====
    'page.setBackground': (hex, mode = 'soon') => apply(() => editor?.changeBackground(hex), mode),
    // PaintPicker gradient recipes → the engine's three-direction verb. The
    // picker is linear-only for the page (pageRow1 descriptor: engine
    // truth); first/last stops become the two colors and the angle snaps to
    // the nearest direction the engine can actually paint (0/180 →
    // horizontal, 90/270 → vertical, else diagonal). Live angle drags ride
    // 'defer' + the flyout's ONE commit on release.
    'page.setBackgroundRecipe': (recipe, mode = 'now') => {
      if (!recipe || recipe.type !== 'linear-gradient') return;
      const stops = recipe.stops || [];
      if (stops.length < 2) return;
      const c1 = stops[0].color;
      const c2 = stops[stops.length - 1].color;
      const a = ((Math.round(recipe.angle ?? 90) % 360) + 360) % 360;
      const near = (t) => Math.min(Math.abs(a - t), 360 - Math.abs(a - t)) <= 22;
      const dir = (near(90) || near(270)) ? 'vertical' : (near(0) || near(180)) ? 'horizontal' : 'diagonal';
      apply(() => editor?.changeBackgroundGradient(c1, c2, dir), mode === 'defer' ? 'defer' : 'now');
    },
    'page.openSizePanel': () => onOpenPageSettings?.(),
    // Bar 0 Poster flyout: W×H in INCHES → ONE changeSize (the verb resizes
    // the workspace, refreshes coords, re-fits the viewport and commits ONE
    // history entry itself) — apply() rides 'defer' purely to re-mirror.
    'page.setSizeInches': (wIn, hIn) => {
      const w = Math.round(Number(wIn) * 72);
      const h = Math.round(Number(hIn) * 72);
      if (!(w > 0) || !(h > 0)) return;
      apply(() => editor?.changeSize({ width: w, height: h }), 'defer');
    },
    // Export sharpness target (150/300/600) — design meta on the workspace
    // data bag; the engine verb validates and commits its own single entry.
    'page.setExportDPI': (v) => apply(() => editor?.setExportDPI(v), 'defer'),

    // ======== Row 2 (R3 sections) ========
    // THE color button (Style section) — routed by kind, one law per kind:
    //   text  → the FillSection routing LAW verbatim: a live range styles
    //           just the highlighted characters (per-char fill, scope chip
    //           law), otherwise the whole-object setTextFill recipe runs
    //           (keeps data.textFill honest where changeFillColor would
    //           bypass the recipe);
    //   icon  → changeIconColor (the tint — the only verb that shows);
    //   shape → changeFillColor (every object in a multi-selection).
    // Swatch picks arrive 'now', color-wheel bursts 'soon' (panel grammar).
    'object.pickColor': (hex, mode = 'now') => {
      if (selectionKind === 'text') {
        apply(
          () => editor?.forSelectionOrObject(
            { fill: hex },
            () => editor?.setTextFill({ type: 'solid', color: hex }),
          ),
          mode,
        );
      } else if (selectionKind === 'icon') {
        // Debounced — changeIconColor self-commits, so the apply grammar can't
        // coalesce its drag ticks (see applyIconTint). The mode threads through:
        // a discrete pick ('now') applies on the leading edge, drag ticks
        // ('soon') coalesce to ONE trailing recolor.
        applyIconTint(hex, mode);
      } else {
        apply(() => editor?.changeFillColor(hex), mode);
      }
    },
    // Gradient/texture recipes — text-only (the panel is Color-pane-only for
    // every other kind) and ALWAYS whole-object (per-char patterns aren't a
    // thing; the flyout shows the hint while a range is live).
    'object.setFillRecipe': (recipe, mode = 'now') => {
      if (selectionKind === 'text') apply(() => editor?.setTextFill(recipe), mode);
    },
    // Outline — the setBorder rule (stroke/strokeWidth/strokeUniform, every
    // object in a multi-selection). Coloring a zero-width outline turns it
    // on at 2 (the old toolbar's affordance); the width slider previews
    // 'defer' and the flyout fires history.commitNow on release; "No
    // outline" clears in one entry.
    // mode arrives from the PaintPicker host: discrete picks 'now', SV/hue
    // drag ticks 'soon' (the pre-standard behavior for every pick).
    'object.setOutlineColor': (hex, mode = 'soon') => setBorder(hex, state.outlineWidth > 0 ? state.outlineWidth : 2, mode),
    'object.setOutlineWidth': (w) => setBorder(state.outlineColor ?? '#000000', w, 'defer'),
    'object.clearOutline': () => setBorder(state.outlineColor ?? '#000000', 0, 'now'),
    // Opacity — THE canonical home, now a flyout slider. Ticks defer;
    // pointer-up commits ONE entry through history.commitNow below.
    'object.setOpacity': (v) => apply(() => editor?.changeOpacity(v), 'defer'),
    // Slider pointer-up (house one-entry rule). Lane N2 rider: a pending
    // non-Rect corner bake flushes HERE — the release is its commit. The bake
    // verb self-batches ONE entry; apply('now') awaits the async WASM and
    // re-mirrors, and the trailing commitNow is a dedupe no-op. Every other
    // slider release hits an empty ref → the plain commitNow, untouched.
    'history.commitNow': () => {
      const r = pendingRoundRef.current;
      if (r != null) {
        pendingRoundRef.current = null;
        if (r > 0) apply(() => editor?.roundShapeCorners(r), 'now');
        else commitNow();
        return;
      }
      commitNow();
    },
    'ui.openPositionPanel': () => onOpenInspector?.('position'),
    // EFFECTS section (owner 2026-07-20; universal U2 2026-07-21) — KIND-ROUTED
    // so ribbon, drawer and styles apply the SAME slot effects. TEXT keeps its
    // handlers verbatim (applyTextEffect/removeTextEffect self-batch, one
    // entry). SHAPE/ICON route the object-generic verbs (applyEffect/
    // removeEffect — kind-routed through the matrix by the engine); the active
    // read is the DUAL bag. Face toggle: a lit slot → remove; else → apply at
    // the def defaults.
    'fx.toggleEffect': (id) => {
      if (selectionKind === 'text') {
        const active = fx.shadow?.id === id || fx.stroke?.id === id;
        if (active) apply(() => editor?.removeTextEffect(id), 'now');
        else apply(() => editor?.applyTextEffect(id, effectDefaults(EFFECT_DEFS[id])), 'now');
      } else {
        const active = fxBag.shadow?.id === id || fxBag.stroke?.id === id;
        if (active) apply(() => editor?.removeEffect(id), 'now');
        else apply(() => editor?.applyEffect(id, effectDefaults(EFFECT_DEFS[id])), 'now');
      }
    },
    // Chip params: an idempotent full re-apply of the merged params (the
    // EffectOptions grammar) — slider ticks defer + commit ONE on release,
    // color 'soon', segmented 'now'. Re-applying while the effect is OFF turns
    // it on at those params (the engine displaces whatever holds the slot).
    // KIND-ROUTED: text → applyTextEffect; shape/icon → the generic applyEffect
    // (merges the def defaults, so the full merged params round-trip idempotent).
    'fx.applyParams': (id, params, mode = 'now') => apply(
      () => (selectionKind === 'text' ? editor?.applyTextEffect(id, params) : editor?.applyEffect(id, params)),
      mode,
    ),
    // Depth faces — the Effects-accordion checkbox verbs verbatim: toggling
    // ON applies the step at its engine default depth (bevel 50 / 3D 55),
    // OFF patches depth 0 (patchTextFx drops the step). ONE entry per tick.
    'depth.toggleBevel': () => apply(
      () => editor?.patchTextFx('bevel', { depth: bevelOn ? 0 : FX_BEVEL_DEFAULTS.depth }),
      'now',
    ),
    'depth.toggle3D': () => apply(
      () => editor?.patchTextFx('extrude', { depth: threeDOn ? 0 : FX_EXTRUDE_DEFAULTS.depth }),
      'now',
    ),
    // Depth flyout dials — the drawer's exact patch grammar (sliders defer +
    // commitNow on release; chips/checks are one entry each). Sliding a dial
    // while the step is OFF turns it on at that value (patchTextFx adds the
    // step) — the flyout is the same dial in a popover.
    'depth.setBevelDepth': (v) => apply(() => editor?.patchTextFx('bevel', { depth: v }), 'defer'),
    'depth.setBevelWidth': (v) => apply(() => editor?.patchTextFx('bevel', { size: v }), 'defer'),
    'depth.setBevelShape': (v) => apply(() => editor?.patchTextFx('bevel', { technique: v }), 'now'),
    'depth.setBevelFinish': (v) => apply(() => editor?.patchTextFx('bevel', { finish: v }), 'now'),
    'depth.setBevelLight': (on) => apply(
      () => editor?.patchTextFx('bevel', { lightMode: on ? 'poster' : 'custom' }),
      'now',
    ),
    'depth.set3DDepth': (v) => apply(() => editor?.patchTextFx('extrude', { depth: v }), 'defer'),
    // Volume style (Depth Everywhere): 'solid' = clear back to plain extrude
    // (sanitize drops the absent style); puffy/pillow ride the same step.
    'depth.setVolumeStyle': (v) => apply(() => editor?.patchTextFx('extrude', {
      volumeStyle: v === 'solid' ? null : v,
    }), 'now'),
    'depth.setVolumeRound': (v) => apply(() => editor?.patchTextFx('extrude', { volumeRound: v }), 'defer'),
    'depth.setVolumeBack': (on) => apply(() => editor?.patchTextFx('extrude', {
      volumeBack: on ? 'round' : 'flat',
    }), 'now'),
    'depth.set3DFinish': (v) => apply(() => editor?.patchTextFx('extrude', { archetype: v }), 'now'),
    // Sticker plate — patchTextFx merges shallow, so the plate object is
    // always sent WHOLE (the drawer's patchPlate rule).
    'depth.setPlateOn': (on) => patchPlate({ enabled: on }, 'now'),
    'depth.setPlateWidth': (v) => patchPlate({ width: v }, 'defer'),
    'depth.setPlateDepth': (v) => patchPlate({ depth: v }, 'defer'),
    // Plate paint (PaintPicker standard): null = match the letters,
    // '#hex' = solid dye, bare manifest id = texture — the fxExtrude plate
    // contract verbatim (fill hex|textureId|null).
    'depth.setPlateFill': (v, mode = 'now') => patchPlate({ fill: v }, mode),
    // Poster light — the document sun's angle (defer + commitNow on release,
    // same grammar as the drawer dial; opted-in objects re-bake via the seam
    // hash inside setPosterLight).
    'page.setPosterLight': (deg) => apply(() => editor?.setPosterLight({ angle: deg }), 'defer'),
    // Arrange
    'arrange.bringForward': () => apply(() => editor?.bringForward()),
    'arrange.sendBackward': () => apply(() => editor?.sendBackwards()),
    'arrange.bringToFront': () => apply(() => editor?.moveLayerToTop(selectedObjects[0])),
    'arrange.sendToBack': () => apply(() => editor?.moveLayerToBottom(selectedObjects[0])),
    'arrange.group': () => apply(() => editor?.group()),
    // Combine 2+ shapes → ONE plain fabric.Path (lane N2). mode: merge (union)
    // / cut (bottom minus the ones on top) / overlap (intersect). The verb
    // self-batches ONE entry; apply('now') awaits the async WASM + re-mirrors.
    'arrange.combine': (mode) => apply(() => editor?.combineShapes(mode), 'now'),
    'arrange.alignToPage': (edge) => {
      const verb = alignVerb[edge];
      if (verb) apply(() => editor?.[verb]?.());
    },
    // Object tail
    'object.toggleLock': () => apply(() => editor?.toggleLock()),
    'object.duplicate': () => apply(() => editor?.duplicate()),
    'object.delete': () => apply(() => editor?.delete()),
    'object.flipH': () => apply(() => editor?.flipHorizontal()),
    'object.flipV': () => apply(() => editor?.flipVertical()),
    // SHELL (R4 implements the copy-the-look behavior; key + mirror fixed
    // now so the rowset never changes when it lands).
    'object.formatPainter': () => {},
  };

  // Drawer-link contract → the EXISTING panel surfaces. Keys are
  // drawerSection ids (group-level or control-level — R3); values
  // [tab, accordionId?]. The accordion targeting only means something for
  // text (TextStyleTab's exclusive group — ids: styles/effects/color/script/
  // fit/phonics/shape/highlight/spacing/more, plus the R2 pseudo-section
  // 'styles-explorer' which opens the explorer modal); the non-text Effects
  // tab has plain sections and ignores it.
  const DRAWER_MAP = {
    // Row 1 (text)
    spacing: ['effects', 'spacing'],
    shape: ['effects', 'shape'], // curve group's fine-tune home
    script: ['effects', 'script'], // x² flyout → Raised & lowered accordion
    fit: ['effects', 'fit'],
    subject: ['effects', 'phonics'],
    // Row 1 (image / shape morphs)
    filters: ['effects'],
    'shape-geometry': ['effects'],
    // Row 2 (R3 sections — control-level footers)
    fill: ['effects', 'color'], // the color flyout's deep home
    outline: ['effects', 'effects'],
    effects: ['effects', 'effects'], // the EFFECTS faces' params footer (owner)
    depth: ['effects', 'effects'], // bevel + 3D fine-tune (sides, lights)
    arrange: ['position'],
    position: ['position'],
  };
  const onOpenDrawer = (sectionId) => {
    const target = DRAWER_MAP[sectionId] || ['effects'];
    onOpenInspector?.(target[0], target[1]);
  };

  return (
    <>
      {/* BAR 0 — always visible, every selection kind (owner 2026-07-21).
          Reads the SAME mirror bag + handler table as the rows below. */}
      <AppBar editor={editor} state={state} handlers={handlers} app={appBar} />
      <Ribbon
        // Row 2 exists ONLY for a selection it applies to (owner directive
        // §1): kind 'none' renders Row 1 alone — the universal row UNMOUNTS,
        // no reserved space (the canvas ResizeObserver picks up the reflow).
        rows={selectionKind === 'none' ? [1] : [1, 2]}
        // Row-1 morph override (Smart Tools epic): a single selected smart
        // object gets its OWN row (the Tool options… button) instead of the
        // generic shape row. Only the ROWSET key changes — selectionKind
        // keeps driving every other policy (fx gates, text reads, disabled).
        selectionKind={smartRecipe ? 'smart' : selectionKind}
        state={state}
        handlers={handlers}
        onOpenDrawer={onOpenDrawer}
        density="normal"
        disabledGroupIds={disabled}
      />
      {photo.node}
      {textureExplorerOpen && (
        <TextureExplorerModal
          objProps={objProps}
          apply={apply}
          commitNow={commitNow}
          editor={editor}
          onClose={() => setTextureExplorerOpen(false)}
        />
      )}
      {fxTexturePick && (() => {
        // Pick-mode session for one effect 'fill' param. Reads the LIVE slot
        // mirror each render, so successive picks preview against the real
        // recipe; scale is preserved across re-picks (the drawer's slider
        // remains the scale home).
        const { effectId, paramKey } = fxTexturePick;
        const def = EFFECT_DEFS[effectId];
        // DUAL bag so a shape/icon sticker's live border texture previews too.
        const entry = fxBag.shadow?.id === effectId ? fxBag.shadow
          : fxBag.stroke?.id === effectId ? fxBag.stroke : null;
        const curVal = entry?.params?.[paramKey];
        const activeId = curVal?.type === 'texture' ? curVal.id : null;
        const onPick = (id) => {
          if (!def) return;
          const merged = {
            ...effectDefaults(def),
            ...(entry?.params || {}),
            [paramKey]: { type: 'texture', id, scale: curVal?.scale ?? 1 },
          };
          // KIND-ROUTED, same as fx.applyParams (the modal is an input method,
          // not a second writer).
          apply(
            () => (selectionKind === 'text'
              ? editor?.applyTextEffect(effectId, merged)
              : editor?.applyEffect(effectId, merged)),
            'now',
          );
        };
        return (
          <TextureExplorerModal
            pickMode
            pickActiveId={activeId}
            onPick={onPick}
            onClose={() => setFxTexturePick(null)}
          />
        );
      })()}
      {swapModal && (
        // Object-swap picker (owner directive §3). Pick → the kind's swap verb
        // (self-committing, so apply 'defer' only re-mirrors after it lands —
        // the changeIconColor invocation posture) → close.
        <ObjectSwapModal
          mode={swapModal.mode}
          activeIcon={swapModal.mode === 'icon' ? (first?.iconName ?? null) : null}
          onPick={(value) => {
            if (swapModal.mode === 'shape') apply(() => editor?.swapShapeType(value), 'defer');
            else apply(() => editor?.swapIcon(value), 'defer');
            setSwapModal(null);
          }}
          onClose={() => setSwapModal(null)}
        />
      )}
      {/* The ONE house color/gradient/texture modal (owner 2026-07-21). Mounted
          once here beside the editor's other modals; every color entry point
          (Row-2 Color chip, Poster background chip, drawer / effect-param
          PaintField) opens it through openPaintModal (paintModalController) —
          no prop-drilling. The lab harness mounts its own instance. */}
      <PaintPickerModalHost />
      {/* The ONE Smart Tools Browser (owner 2026-07-23: "a full smart tools
          browser with categorization, tagging etc, because 220 tools is far too
          much to add to the left hand drawer"). Mounted once beside the other
          modal hosts; the Smart drawer's "Browse all" button (and any future
          ribbon entry) opens it through openSmartToolsBrowser
          (smartToolsBrowserController) — no prop-drilling. */}
      <SmartToolsBrowserHost />
    </>
  );
}
